import React, { useEffect, useRef, useState } from 'react';

import SettingsIcon from '@mui/icons-material/Settings';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import {
    Alert,
    Box,
    Card,
    IconButton,
    Tooltip,
    UiDialog as Dialog,
} from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import InputSearch from 'common/components/InputSearch';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';

import PreferenceToggle from './PreferenceToggle';
import ProductColumns from './ProductColumns';

const ListItems = ({
    filterData,
    filterItems,
    handleClickUpdate,
    isFetching,
    items,
    setFilterData,
}) => {
    const { t } = useTranslation();
    const [currentFilterData, setCurrentFilterData] = useState(filterData);
    const [isOpenModal, setIsOpenModal] = useState(false);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) =>
        setFilterData((prev) => ({
            ...prev,
            page: 0,
            searchText: event.target.value,
        }));

    useEffect(() => {
        if (isEqual(currentFilterData, filterData)) return;
        handleOnFilter(filterData);
        setCurrentFilterData(filterData);
    }, [filterData]);

    const columns = ProductColumns({
        handleClickUpdate,
    });
    const getRowHeight = () => 'auto';
    const CustomToolbar = () => (
        <GridToolbarContainer>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={t('System settings')}>
                <IconButton
                    color="primary"
                    onClick={() => setIsOpenModal(true)}
                    size="small"
                >
                    <SettingsIcon />
                </IconButton>
            </Tooltip>
        </GridToolbarContainer>
    );

    return (
        <Card>
            <Card.Body>
                <Box my={2}>
                    <InputSearch
                        placeholder={t('Search')}
                        onChange={handleOnChangeSearch}
                        value={filterData?.searchText}
                    />
                </Box>
                <DataGrid
                    checkboxSelection={false}
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    editMode="row"
                    getRowHeight={getRowHeight}
                    hideFooterSelectedRowCount
                    loading={isFetching}
                    pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
                    rows={items}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                />
            </Card.Body>
            <Dialog
                onClose={() => setIsOpenModal(false)}
                open={isOpenModal}
                size="md"
                title={t('System settings')}
            >
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t(
                        'This configuration determines which preset will be applied in the advanced settings of each system, allowing you to choose the source from which these settings will be taken',
                    )}
                </Alert>
                <PreferenceToggle />
            </Dialog>
        </Card>
    );
};

ListItems.propTypes = {
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    handleClickUpdate: PropTypes.func,
    isFetching: PropTypes.bool,
    items: PropTypes.array,
    setFilterData: PropTypes.func,
};

export default ListItems;
